import React, { useEffect, useMemo, useRef, useState } from 'react';
import Rest from 'tools/rest';
import { Row, Col, Typography, Form, Input, Button, message, Tabs, Space, Tooltip } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  LinkOutlined,
} from '@ant-design/icons';

import Preloader from 'components/preloader';

const { Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const PagePartCard = ({ pagePartId, onUpdated }) => {
  const [pagePart, setPagePart] = useState(null);
  const [formPagePart, setFormPagePart] = useState(null);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const contentEditorRef = useRef(null);

  const buildSnapshot = (record) => ({
    name: record?.name || '',
    content_type: record?.content_type || '',
    content: record?.content || '',
  });

  const loadPagePart = () => {
    if (!pagePartId) return;

    setLoading(true);
    Rest.get(`/api/v1/page_parts/${pagePartId}.json`).then((response) => {
      const loadedPagePart = response.data.page_part;
      const snapshot = buildSnapshot(loadedPagePart);
      setPagePart(loadedPagePart);
      setFormPagePart(snapshot);
      setInitialSnapshot(snapshot);
      setErrors({});
    }).catch((e) => {
      console.error('error', e);
    }).finally(() => {
      setLoading(false);
    });
  };

  const hasChanges = useMemo(() => {
    if (!formPagePart || !initialSnapshot) return false;
    return JSON.stringify(formPagePart) !== JSON.stringify(initialSnapshot);
  }, [formPagePart, initialSnapshot]);

  const handleChangeText = (e) => {
    const { name, value } = e.target;
    setFormPagePart((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const getContentTextarea = () => contentEditorRef.current?.resizableTextArea?.textArea;

  const wrapSelection = (before, after = '') => {
    const textarea = getContentTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const currentContent = formPagePart?.content || '';
    const selected = currentContent.slice(start, end) || 'текст';
    const nextContent = `${currentContent.slice(0, start)}${before}${selected}${after}${currentContent.slice(end)}`;

    setFormPagePart((prev) => ({ ...prev, content: nextContent }));
    setErrors((prev) => ({ ...prev, content: null }));

    requestAnimationFrame(() => {
      const nextStart = start + before.length;
      const nextEnd = nextStart + selected.length;
      textarea.focus();
      textarea.setSelectionRange(nextStart, nextEnd);
    });
  };

  const insertAtCursor = (markup) => {
    const textarea = getContentTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const currentContent = formPagePart?.content || '';
    const nextContent = `${currentContent.slice(0, start)}${markup}${currentContent.slice(end)}`;

    setFormPagePart((prev) => ({ ...prev, content: nextContent }));
    setErrors((prev) => ({ ...prev, content: null }));

    requestAnimationFrame(() => {
      const cursor = start + markup.length;
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const handleUpdatePagePart = () => {
    if (!pagePartId || !formPagePart) return;

    const payload = {
      name: formPagePart.name,
      content_type: formPagePart.content_type || null,
      content: formPagePart.content,
    };

    setLoading(true);
    Rest.put(`/api/v1/page_parts/${pagePartId}.json`, { page_part: payload }).then((response) => {
      const updatedPagePart = response.data.page_part;
      const snapshot = buildSnapshot(updatedPagePart);
      setPagePart(updatedPagePart);
      setFormPagePart(snapshot);
      setInitialSnapshot(snapshot);
      setErrors({});
      message.success('Запись обновлена');
      if (onUpdated) onUpdated(updatedPagePart);
    }).catch((e) => {
      setErrors(e?.response?.data?.page_part?.errors || {});
      message.error('Ошибка обновления записи');
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    loadPagePart();
  }, [pagePartId]);

  return (
    <Preloader loading={loading}>
      <Row gutter={[0, 12]}>
        <Col span={24}>
          <Form
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
          >
            <Form.Item label="ID">
              <Text>{pagePart?.id}</Text>
            </Form.Item>
            <Form.Item
              label="Ключ"
              help={errors?.name && errors.name.join(', ')}
              validateStatus={errors?.name ? 'error' : ''}
            >
              <Input
                name="name"
                value={formPagePart?.name}
                onChange={handleChangeText}
                placeholder="Название части страницы"
              />
            </Form.Item>
            <Form.Item
              label="Тип"
              help={errors?.content_type && errors.content_type.join(', ')}
              validateStatus={errors?.content_type ? 'error' : ''}
            >
              <Input
                name="content_type"
                value={formPagePart?.content_type}
                onChange={handleChangeText}
                placeholder="Например: text/html"
              />
            </Form.Item>
            <Form.Item
              label="Контент (HTML)"
              help={errors?.content && errors.content.join(', ')}
              validateStatus={errors?.content ? 'error' : ''}
            >
              <Tabs defaultActiveKey="html">
                <TabPane tab="HTML-разметка" key="html">
                  <Space wrap style={{ marginBottom: 8 }}>
                    <Tooltip title="Жирный">
                      <Button icon={<BoldOutlined />} onClick={() => wrapSelection('<strong>', '</strong>')} />
                    </Tooltip>
                    <Tooltip title="Курсив">
                      <Button icon={<ItalicOutlined />} onClick={() => wrapSelection('<em>', '</em>')} />
                    </Tooltip>
                    <Tooltip title="Подчеркнутый">
                      <Button icon={<UnderlineOutlined />} onClick={() => wrapSelection('<u>', '</u>')} />
                    </Tooltip>
                    <Tooltip title="Зачеркнутый">
                      <Button icon={<StrikethroughOutlined />} onClick={() => wrapSelection('<s>', '</s>')} />
                    </Tooltip>
                    <Tooltip title="Маркированный список">
                      <Button icon={<UnorderedListOutlined />} onClick={() => wrapSelection('<ul><li>', '</li></ul>')} />
                    </Tooltip>
                    <Tooltip title="Нумерованный список">
                      <Button icon={<OrderedListOutlined />} onClick={() => wrapSelection('<ol><li>', '</li></ol>')} />
                    </Tooltip>
                    <Tooltip title="Ссылка">
                      <Button icon={<LinkOutlined />} onClick={() => wrapSelection('<a href=\"https://example.com\" target=\"_blank\" rel=\"noreferrer\">', '</a>')} />
                    </Tooltip>
                    <Button onClick={() => wrapSelection('<h3>', '</h3>')}>H3</Button>
                    <Button onClick={() => wrapSelection('<p>', '</p>')}>P</Button>
                    <Button onClick={() => insertAtCursor('<br />')}>BR</Button>
                  </Space>
                  <TextArea
                    ref={contentEditorRef}
                    name="content"
                    value={formPagePart?.content}
                    onChange={handleChangeText}
                    rows={12}
                    style={{ fontFamily: 'monospace' }}
                  />
                </TabPane>
                <TabPane tab="Предпросмотр" key="preview">
                  <div
                    style={{ wordBreak: 'break-word', minHeight: 160 }}
                    dangerouslySetInnerHTML={{ __html: formPagePart?.content || '' }}
                  />
                </TabPane>
              </Tabs>
            </Form.Item>
            {hasChanges && (
              <Form.Item wrapperCol={{ offset: 4, span: 20 }}>
                <Button type="primary" onClick={handleUpdatePagePart}>
                  Сохранить
                </Button>
              </Form.Item>
            )}
          </Form>
        </Col>
      </Row>
    </Preloader>
  );
};

export default PagePartCard;
